import React from 'react';
import styles from './auditLogFormatters.module.scss';
import _ from 'lodash';
import i18n from 'i18n';
import { State } from 'core/order/Order';
import { AUDITLOG_SRC } from 'core/auditLog/AuditLog';
import {
  DeliverType,
  CampaignState,
  CreativeDeliverType,
  AdType
} from 'core/rtbCampaign/RtbCampaign';
import { getDayOfWeekLabelByValue } from 'components/Dayparts/Dayparts';
import moment from 'moment';
import { CreativeType } from 'core/creative/Creative';
import { AgencyRole } from 'core/companyMember/CompanyRole';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import { ChannelGroupStatus } from 'core/channelGroup/ChannelGroup';
import { getChannelGroupName } from 'components/AgencyForm/agencyAddonVisibility';
import { getSalesChannelName } from 'utils/I18nUtils';

const srcInfoFormatter = (srcInfo: {
  id: number;
  type: string;
  name?: string;
}) => {
  return (
    <div className={styles.srcInfo}>
      {srcInfo.name ? (
        <div>
          {i18n.t(`auditLog.labels.src${srcInfo.type}`) + ':' + srcInfo.name}
        </div>
      ) : (
        <div>{i18n.t(`auditLog.labels.src${srcInfo.type}`)}</div>
      )}
      <div className={styles.bottomInfo}>{`ID: ${srcInfo.id}`}</div>
    </div>
  );
};

const i18nFormatter = (i18nKey) => {
  return <>{i18n.t(i18nKey)}</>;
};

function getAdTypeValue(adType) {
  const key = `campaignSummary.labels.adType${_.upperFirst(_.camelCase(adType))}`;
  if (!i18n.exists(key)) {
    return adType;
  }

  return i18n.t(key);
}

function getDeliverTypeValue(deliverType) {
  switch (deliverType) {
    case DeliverType.ACCELERATED:
      return i18n.t('campaignSummary.labels.accelerated');
    default:
      return i18n.t('campaignSummary.labels.standard');
  }
}

function getCreativeDeliverTypeValue(creativeDeliverType: number) {
  return i18n.t(
    `campaign.labels.creativeDeliverType.${CreativeDeliverType[creativeDeliverType].toLowerCase()}`
  );
}

function getDayPartValue(orgValue: string) {
  try {
    const dayPart = JSON.parse(orgValue);
    const dayPartValue = _.omitBy(_.omit(dayPart, 'enabled'), _.isEmpty);
    return (
      <div style={{ whiteSpace: 'pre' }}>
        {Object.keys(dayPartValue)
          .map((day) => {
            return `${getDayOfWeekLabelByValue(parseInt(day, 10))},${i18n.t('daypart.labels.hourUnit')}: ${dayPartValue[day].join(', ')}`;
          })
          .join('\r\n')}
      </div>
    );
  } catch (e) {
    return orgValue.toString();
  }
}

function getFrequencyValue(orgValue) {
  try {
    const frequencyControl = JSON.parse(orgValue);
    if (!frequencyControl.intervalDays && !frequencyControl.maxFrequency) {
      return i18n.t('auditLog.labels.frequencyNoEnable');
    }
    return i18n.t('adSetSetupFlow.mainStep.labels.frequencyControl', {
      event: i18n.t('adSetSetupFlow.mainStep.labels.impression'),
      eventPlural: +frequencyControl.maxFrequency > 1 ? 's' : '',
      interval_days: frequencyControl.intervalDays,
      max_frequency: frequencyControl.maxFrequency,
      unit: i18n.t('common.units.day'),
      unitPlural: +frequencyControl.intervalDays > 1 ? 's' : ''
    });
  } catch (e) {
    return orgValue.toString();
  }
}

function getAdLogoValue(value) {
  try {
    const adLogo = JSON.parse(value);
    if (adLogo.type === 'NULL') {
      return i18n.t('common.labels.noData');
    }
    if (adLogo.type === 'DEFAULT') {
      return i18n.t('common.labels.default');
    }
    if (adLogo.type === 'CUSTOM') {
      return adLogo.imgUrl;
    }
    return value.toString();
  } catch (e) {
    return value.toString();
  }
}

function getCampaignBidPriceValue(value) {
  const renderBidPriceSetting = (bidPriceSetting: any) => {
    return (
      <div className={styles.bidPriceContainer}>
        {Object.keys(bidPriceSetting).map((optimize, index) => {
          return (
            <div key={index} className={styles.bidPrice}>
              <span>
                {i18n.t(`bidPriceSetting.optimize.${optimize.toLowerCase()}`)}
              </span>
              <span>{bidPriceSetting[optimize]}</span>
            </div>
          );
        })}
      </div>
    );
  };
  try {
    const campaignBidPrice = JSON.parse(value);
    return campaignBidPrice.map((bidPrice, index) => {
      const autoBidCap = _.get(bidPrice, 'autoBidCap', {});
      const bidFloor = _.get(bidPrice, 'bidFloor', {});
      const type = _.get(bidPrice, 'type', '');
      const i18nKey = `bidPriceSetting.labels.${_.camelCase(type)}`;
      const adTypeId = _.get(bidPrice, 'adTypeId');
      let title = i18n.exists(i18nKey) ? i18n.t(i18nKey) : type;
      if (adTypeId) {
        const adTypeSetting = ChannelGroupSetting.adTypeSettings().get(
          adTypeId.toString()
        );
        if (adTypeSetting) {
          title = adTypeSetting.name;
        }
      }
      return (
        <div key={index} className={styles.campaignBidPrice}>
          <div>{title}</div>
          <div className={styles.settings}>
            <span>{i18n.t('bidPriceSetting.labels.autoBidCapSetting')}</span>
            {renderBidPriceSetting(autoBidCap)}
          </div>
          <div className={styles.settings}>
            <span>{i18n.t('bidPriceSetting.labels.bidFloorSetting')}</span>
            {renderBidPriceSetting(bidFloor)}
          </div>
        </div>
      );
    });
  } catch (e) {
    return value.toString();
  }
}

function getAgencyRoleValue(value) {
  try {
    return i18n.t(`agency.labels.${AgencyRole[value].toLowerCase()}`);
  } catch (e) {
    return value.toString();
  }
}

function getViewTrackingCodesValue(value) {
  try {
    const trackingCodes = JSON.parse(value);
    return trackingCodes.map((code, index) => {
      return <div key={index}>{code.trackingCode}</div>;
    });
  } catch (e) {
    return value.toString();
  }
}

function isValueEmpty(value) {
  if (_.isNil(value) || value === '' || value === '{}' || value === 'null') {
    return true;
  }

  return false;
}

function getVideoAdViewObjectiveValue(value) {
  try {
    const videoAdViewObjective = JSON.parse(value);
    if (videoAdViewObjective.event) {
      return i18n.t('campaignSummary.labels.videoAdViewObjectiveMetric', {
        metric: i18n.t(
          `campaign.labels.${videoAdViewObjective.event.toLowerCase()}`
        )
      });
    }
  } catch (e) {
    return value.toString();
  }
}

function getVideoProgressTrackingCodeValue(value) {
  try {
    const videoProgressTrackingCode = JSON.parse(value)[0];
    return (
      <div>
        <div className={styles.videoProgressTrackingCode}>
          <div>
            {i18n.t('campaignSummary.labels.videoProgressTrackingCode')}:{' '}
          </div>
          <div>{videoProgressTrackingCode.code}</div>
        </div>
        <div>
          {i18n.t('campaignSummary.labels.videoProgressTrackingOffset')}:{' '}
          {`${videoProgressTrackingCode.offset} ${i18n.t('campaign.labels.sec')}`}
        </div>
      </div>
    );
  } catch (e) {
    return value.toString();
  }
}

export const getAddonPropsLabel = (addonItem: string): any => {
  if (addonItem === 'targetBudgetMinimum') {
    return i18n.t('agency.targetBudgetMinimum');
  }
  if (addonItem === 'campaignBudgetMinimum') {
    return i18n.t('agency.campaignBudgetMinimum');
  }
  if (addonItem === 'orderBudgetMaximum') {
    return (
      `${i18n.t('agency.addonGroup.company')}` +
      `[${i18n.t('agency.addonItem.company.singleOrderBudgetConstraint')}${i18n.t('agency.addonItem.company.orderBudgetMaximum')}]`
    );
  }
  if (addonItem === 'creditLimit') {
    return (
      `${i18n.t('agency.addonGroup.company')}` +
      `[${i18n.t('agency.addonItem.company.creditLimitMaximum')}]`
    );
  }
  if (addonItem === 'maxOrderProfit') {
    return i18n.t('agency.addonProps.maxOrderProfit');
  }
  if (addonItem === 'fixedExchangeRate') {
    return (
      i18n.t('agency.addonGroup.company') +
      `[${i18n.t('agency.addonItem.company.fixedExchangeRate')}]`
    );
  }
  if (addonItem === 'campaignBidPrice') {
    return (
      i18n.t('agency.addonGroup.campaign') +
      `[${i18n.t('agency.addonItem.campaign.bidPriceConstraint')}]`
    );
  }
  if (addonItem === 'agencySegmentLimitationName') {
    return (
      i18n.t('agency.addonGroup.company') +
      `[${i18n.t('agency.addonItem.company.agencySegmentLimitationName')}]`
    );
  }
};

// addonGroup === 'channel': addonItem 是 channel id，從 addon config 解析出 channel 名稱。
const getChannelAddonLabel = (
  labelPrefix: string,
  addonItem: string
): string => {
  const channelGroupAddonConfigs = ChannelGroupSetting.getAddonConfigs();
  if (addonItem in channelGroupAddonConfigs) {
    const { channelId, channelName } = channelGroupAddonConfigs[addonItem];
    return (
      labelPrefix + `[${getSalesChannelName(channelId ?? '', channelName)}]`
    );
  }
  return labelPrefix + `[unknown channel id: ${addonItem}]`;
};

// addonGroup === 'creatives' 且 key 形如 option_${creativeType}。
const getCreativeAddonLabel = (
  labelPrefix: string,
  addonGroup: string,
  addonItem: string
): string => {
  const creativeType = addonItem.split('_')[1];
  if (creativeType in CreativeType) {
    const addonName = `option_${_.camelCase(CreativeType[creativeType])}`;
    return (
      labelPrefix + `[${i18n.t(`agency.addonItem.${addonGroup}.${addonName}`)}]`
    );
  }
  const creativeTypeSetting = ChannelGroupSetting.creativeTypeSettings().get(
    Number(creativeType)
  );
  return labelPrefix + `[${creativeTypeSetting?.name || 'unkown'}]`;
};

// addonGroup === 'adTypes'：key 形如 adType_${adType}。
const getAdTypeAddonLabel = (
  labelPrefix: string,
  addonItem: string
): string => {
  const adType = addonItem.replace('adType_', '');
  if (adType in AdType) {
    return labelPrefix + `[${i18n.t(`adType.${adType.toLowerCase()}`)}]`;
  }
  const adTypeSetting = ChannelGroupSetting.adTypeSettings().get(adType);
  return labelPrefix + `[${adTypeSetting?.name || 'unkown'}]`;
};

// 其餘 addon：可能是 per-channel 扁平 key（channel_${id}_${addonName}），
// 此時 i18n 只用 addonName 組，label 後綴加上該 channel group 名稱；
// 否則直接用 addonItem 當 i18n key。
const getGenericAddonLabel = (
  labelPrefix: string,
  addonGroup: string,
  addonItem: string
): string => {
  const channelMatch = /^channel_(\d+)_(.+)$/.exec(addonItem);
  if (!channelMatch) {
    return (
      labelPrefix + `[${i18n.t(`agency.addonItem.${addonGroup}.${addonItem}`)}]`
    );
  }
  const label =
    labelPrefix +
    `[${i18n.t(`agency.addonItem.${addonGroup}.${channelMatch[2]}`)}]`;
  const channelGroup = ChannelGroupSetting.values().find(
    (group) => group.config.id === Number(channelMatch[1])
  );
  return channelGroup
    ? `${label} (${getChannelGroupName(channelGroup)})`
    : label;
};

// pattern: `addonFeatures.${addonGroup}.${addonName}`
const getAddonFeatureLabel = (
  addonGroup: string,
  addonItem: string
): string => {
  const labelPrefix = i18n.t(`agency.addonGroup.${addonGroup}`);
  if (addonGroup === 'channel') {
    return getChannelAddonLabel(labelPrefix, addonItem);
  }
  if (addonGroup === 'creatives' && addonItem.indexOf('option_') >= 0) {
    return getCreativeAddonLabel(labelPrefix, addonGroup, addonItem);
  }
  if (addonGroup === 'adTypes') {
    return getAdTypeAddonLabel(labelPrefix, addonItem);
  }
  return getGenericAddonLabel(labelPrefix, addonGroup, addonItem);
};

const getPropertyLabel = (field: string, i18nKeys: string[]): string => {
  if (field.indexOf('property.addonProps.channelMargin.') >= 0) {
    const channelId = i18nKeys[i18nKeys.length - 1];
    return (
      i18n.t('agency.addonGroup.channel') +
      `[${getSalesChannelName(channelId)}
          -${i18n.t('agency.addonItem.channel.salesChannelMargin')}]`
    );
  }
  // pattern: `property.${propertyName}` || `property.addonProps.${propertyName}`
  return getAddonPropsLabel(i18nKeys[i18nKeys.length - 1]);
};

// ECOPD-5701 review：渠道 audit log 的欄位 label。
// 不能沿用 `getAgencyLogLabel` —— 它的 addon 分支組出來的是經銷商的
// `agency.addonItem.*`，渠道另有一套 `channelGroupForm.labels.*` / `channelGroup.labels.*`。
// 一律只用末段查找：i18next 的 `keySeparator` 是 '.'，locale 裡寫扁平的
// `"property.addonProps.defaultMargin"` 會被當成巢狀路徑而查不到。
const CHANNEL_GROUP_LABEL_SOURCES: Record<string, string> = {
  status: 'auditLog.labels.channelGroupStatus',
  channelId: 'auditLog.labels.channelGroupId',
  channelType: 'auditLog.labels.channelGroupType',
  campaignAdTypeSettings: 'auditLog.labels.campaignAdTypeSettings',
  reportIgnoredDV360Items: 'auditLog.labels.reportIgnoredDV360Items',
  campaignBidPrice: 'auditLog.labels.campaignBidPrice',
  screenPixelWidth: 'auditLog.labels.screenPixelWidth',
  screenPixelHeight: 'auditLog.labels.screenPixelHeight',
  // 渠道頁把這兩個欄位合成一列「實際播放尺寸」顯示，但 audit log 是逐欄位比對，
  // 各自要有自己的字面；沒有的話會退成欄位名本身。
  compositePixelWidth: 'auditLog.labels.compositePixelWidth',
  compositePixelHeight: 'auditLog.labels.compositePixelHeight',
  // 有現成字面，但末段與 i18n key 拼法不同，走不到下面的候選順序。
  outdoorCreativeSizes: 'channelGroup.labels.creativeSizes',
  epaperDefaultImageUrl: 'channelGroupForm.labels.epaperDefaultImage'
};

// 品牌設定的末段（`en` / `ja` / `url`）太短，放進上面那張共用表會跟其他欄位撞名，
// 所以只在 `property.addonProps.brand.` 底下查這一張。
// 語系的末段依後端屬性名是 `zhTW`，表單 i18n key 用的是 `zh-tw`。
const CHANNEL_GROUP_BRAND_LABEL_SOURCES: Record<string, string> = {
  brand: 'channelGroups.titles.brandContent',
  ja: 'channelGroupForm.labels.brandNameI18n.ja',
  en: 'channelGroupForm.labels.brandNameI18n.en',
  zhTW: 'channelGroupForm.labels.brandNameI18n.zh-tw',
  url: 'channelGroupForm.labels.brandUrl',
  filteredIconUrl: 'channelGroupForm.labels.brandFilteredIcon',
  normalIconUrl: 'channelGroupForm.labels.brandNormalIcon'
};
const CHANNEL_GROUP_BRAND_PATH = 'property.addonProps.brand';

const getChannelGroupBrandLogLabel = (leaf: string): string => {
  const key = CHANNEL_GROUP_BRAND_LABEL_SOURCES[leaf];
  return key && i18n.exists(key) ? i18n.t(key) : leaf;
};

export const getChannelGroupLogLabel = (field: string): string => {
  const segments = field.split('.');
  const leaf = segments[segments.length - 1];

  if (
    field === CHANNEL_GROUP_BRAND_PATH ||
    field.startsWith(`${CHANNEL_GROUP_BRAND_PATH}.`)
  ) {
    return getChannelGroupBrandLogLabel(leaf);
  }

  // 後端送 `nameI18n.zhTW`，表單的 key 卻是 `nameI18n.zh-tw`。
  if (segments[0] === 'nameI18n') {
    const language = leaf === 'zhTW' ? 'zh-tw' : leaf;
    const languageKey = `channelGroupForm.labels.nameI18n.${language}`;
    return i18n.exists(languageKey) ? i18n.t(languageKey) : leaf;
  }

  const candidates = [
    CHANNEL_GROUP_LABEL_SOURCES[leaf],
    `auditLog.labels.${field}`,
    `channelGroupForm.labels.${leaf}`,
    `channelGroup.labels.${leaf}`
  ];
  const hit = candidates.find((key) => key && i18n.exists(key));

  // 查不到就退成欄位名，不要把整串 i18n key 吐到畫面上。
  return hit ? i18n.t(hit) : leaf;
};

export const getAgencyLogLabel = (field: string): string => {
  const i18nKeys = field.split('.');
  if (i18nKeys.length === 1) {
    return i18n.t(`auditLog.labels.${field}`);
  }
  if (field.indexOf('addonFeatures.') >= 0) {
    return getAddonFeatureLabel(i18nKeys[1], i18nKeys[2]);
  }
  if (field.indexOf('property.') >= 0) {
    return getPropertyLabel(field, i18nKeys);
  }
  return '';
};

// 物件陣列，走通用的 `join(',')` 只會得到 `[object Object]`。
function getCampaignAdTypeSettingsValue(value) {
  if (!Array.isArray(value)) {
    return String(value);
  }
  return value
    .map((setting) => {
      const name = _.get(setting, 'name');
      if (name) {
        return name;
      }
      const adTypeId = _.get(setting, 'adTypeId');
      const adTypeSetting = adTypeId
        ? ChannelGroupSetting.adTypeSettings().get(String(adTypeId))
        : undefined;
      // 讀不到就吐 JSON，不要靜默變成空字串。
      return adTypeSetting?.name ?? JSON.stringify(setting);
    })
    .join(', ');
}

// 元素是 `{ width, height }`（後端 `OutdoorCreativeSize`）。
function getOutdoorCreativeSizesValue(value) {
  if (!Array.isArray(value)) {
    return String(value);
  }
  return value
    .map((size) => {
      const width = _.get(size, 'width');
      const height = _.get(size, 'height');
      return width && height ? `${width}×${height}` : JSON.stringify(size);
    })
    .join(', ');
}

function getValue(value, type?: any, key?: any) {
  if (isValueEmpty(value)) {
    return i18n.t('common.labels.noData');
  }

  if (type === AUDITLOG_SRC.ORDER && key === 'state') {
    if (State[value] && typeof value === 'number') {
      return i18n.t(
        `order.status.${State[value].toLowerCase().replace(/_/g, '')}`
      );
    }
    return value;
  }

  if (type === AUDITLOG_SRC.CAMPAIGN && key === 'state') {
    if (CampaignState[value] && typeof value === 'number') {
      return i18n.t(
        `campaignList.labels.${CampaignState[value].toLowerCase()}State`
      );
    }
    return value;
  }

  if (type === AUDITLOG_SRC.CAMPAIGN) {
    switch (key) {
      case 'adLogo':
        return getAdLogoValue(value);
      case 'adType':
        return getAdTypeValue(value);
      case 'deliverType':
        return getDeliverTypeValue(value);
      case 'creativeDeliverType':
        return getCreativeDeliverTypeValue(value);
      case 'dayPart':
        return getDayPartValue(value);
      case 'frequency':
        return getFrequencyValue(value);
      case 'startDate':
      case 'endDate':
        return moment(value).format('YYYY-MM-DD HH:mm:ss');
      case 'priceModel':
        return i18n.t(`campaign.labels.${value}`);
      case 'viewTrackingCodes':
        return getViewTrackingCodesValue(value);
      case 'videoProgressTrackingCode':
        return getVideoProgressTrackingCodeValue(value);
      case 'videoAdViewObjective':
        return getVideoAdViewObjectiveValue(value);
    }
  }

  if (type === AUDITLOG_SRC.AGENCY) {
    switch (key) {
      case 'adLogo':
        return getAdLogoValue(value);
      case 'property.addonProps.campaignBidPrice':
        return getCampaignBidPriceValue(value);
    }
  }

  if (type === AUDITLOG_SRC.COMPANY_ROLE_MAP) {
    switch (key) {
      case 'roleName':
        return getAgencyRoleValue(value);
    }
  }

  // ECOPD-5701：渠道 audit log 裡「停用與否」的三個欄位都要顯示成中文狀態。
  //
  // ⚠️ 少一個 case 的後果是**畫面直接吐出原始值**（掉到最後的 `value.toString()`），
  // 不會報錯也不會有測試變紅 —— e2e S15 就是這樣抓到 `effectiveArchived` 的。
  // 後端若之後再往這條 audit log 加停用相關欄位，記得一起補進來。
  if (type === AUDITLOG_SRC.CHANNEL_GROUP) {
    switch (key) {
      // 布林。後端有可能送布林、也可能送字串，所以用 String() 正規化後再比。
      case 'pmaxArchived':
      case 'effectiveArchived':
        return String(value) === 'true'
          ? i18n.t('channelGroupDetail.labels.archiveStatusArchived')
          : i18n.t('channelGroupDetail.labels.archiveStatusActive');
      // RMax 側的封存狀態。後端 `ChannelGroup.status` 是 `short`（1=啟用/2=封存），
      // 由 `ChannelGroupSyncService` 在同步時寫進 diff，送過來就是裸數字。
      case 'status':
        return Number(value) === ChannelGroupStatus.ARCHIVED
          ? i18n.t('channelGroupDetail.labels.archiveStatusArchived')
          : i18n.t('channelGroupDetail.labels.archiveStatusActive');
      // 對齊詳情頁「基本資訊」的是／否（`ChannelGroupDetail.tsx:129` / `:140` / `:222`），
      // 否則同一頁兩個 tab 對同一個欄位會一個寫「是」、一個寫 `true`。
      //
      // 只有布林欄位放這裡。`compositePixelWidth` / `compositePixelHeight` 是數字，
      // 落到最後的 `value.toString()` 才是正確顯示。
      case 'addonFeatures.campaign.limitPublisherId':
      case 'addonFeatures.campaign.supportPMP':
      case 'addonFeatures.campaign.supportCBO':
      case 'addonFeatures.campaign.supportWeatherTargeting':
      case 'addonFeatures.report.salesChannelReportShowOverspend':
      case 'screenProperty.isMultiScreenSameVideo':
      case 'screenProperty.isScreenSpliced':
      case 'screenProperty.isCompositeCreative':
        return String(value) === 'true'
          ? i18n.t('common.labels.yes')
          : i18n.t('common.labels.no');
      // 與經銷商共用：propertyName 逐字相同，後端兩支 visitor 也同樣轉成 JSON 字串。
      case 'property.addonProps.campaignBidPrice':
        return getCampaignBidPriceValue(value);
      case 'campaignAdTypeSettings':
        return getCampaignAdTypeSettingsValue(value);
      case 'property.addonProps.outdoorCreativeSizes':
        return getOutdoorCreativeSizesValue(value);
    }
  }

  // `join(',')` 對物件陣列會靜默變成 `[object Object]`。這條守的不是上面那兩個
  // 欄位（各自有 formatter），而是日後新增的物件陣列欄位至少看得到內容。
  if (Array.isArray(value)) {
    return value.every((item) => item === null || typeof item !== 'object')
      ? value.join(',')
      : JSON.stringify(value);
  }

  return value.toString();
}

const detailExpandRenderer = (audiLogRow) => {
  if (!audiLogRow.msg) {
    return <div />;
  }
  let detailDataArray = audiLogRow.msg;
  if (!Array.isArray(audiLogRow.msg) && typeof audiLogRow.msg === 'object') {
    detailDataArray = Object.keys(audiLogRow.msg).map((key) => {
      const value = audiLogRow.msg[key];
      const values = value.split('=>');
      return {
        [key]: {
          org: values[0].trim(),
          to: values[1].trim()
        }
      };
    });
  }
  const detail = detailDataArray.map((data) => {
    const keys = Object.keys(data);
    const key = keys[0];
    const value = data[keys[0]];
    if (isValueEmpty(value.org) && isValueEmpty(value.to)) {
      return <div key={key} />;
    }
    let label = i18n.t(`auditLog.labels.${key}`);
    if (audiLogRow.funcType === 'auditLog.limitation.create') {
      const i18nKeys = key.split('.');
      label =
        i18n.t(`limitation.labels.${i18nKeys[0]}`) +
        `(${i18n.t(`limitation.${i18nKeys[1]}`)})`;
    } else if (key.indexOf('_value') >= 0) {
      const i18nKeys = key.split('_');
      label = i18n.t(`limitation.labels.${i18nKeys[0]}`);
    }
    if (audiLogRow.srcID.type === AUDITLOG_SRC.AGENCY) {
      label = getAgencyLogLabel(key);
    } else if (audiLogRow.srcID.type === AUDITLOG_SRC.CHANNEL_GROUP) {
      label = getChannelGroupLogLabel(key);
    }
    return (
      <div className={styles.field} key={key}>
        <div className={styles.fieldLabel}>{label}</div>
        <div className={styles.from}>
          <div>{i18n.t('auditLog.labels.from')}</div>
          <div>:</div>
          <div>{getValue(value.org, audiLogRow.srcID.type, key)}</div>
        </div>
        <div className={styles.to}>
          <div>{i18n.t('auditLog.labels.to')}</div>
          <div>:</div>
          <div>{getValue(value.to, audiLogRow.srcID.type, key)}</div>
        </div>
      </div>
    );
  });
  return <div className={styles.detailRenderer}>{detail}</div>;
};

const formatters = {
  srcInfoFormatter,
  detailExpandRenderer,
  i18nFormatter
};

export default formatters;
